import { Command } from 'cmdk';
import { Bot, FileText, Users, Workflow } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { InputGroup, InputGroupAddon } from '../tailgrids/core/input-group.js';
import { cn } from '../../utils/cn.js';
import { useAuthStore } from '../../store/index.js';
import { evaluateClientPolicy } from '../../auth/policies.js';
import { usePolicy } from '../../hooks/usePolicy.js';
import { useAllEnabledNavItems } from '../../modules/useNavItems.js';
import { resolveIcon } from '../../modules/icons.js';
import { type EntitySearchResult, useEntitySearch } from '../../hooks/useEntitySearch.js';
import { buildNavTree } from './nav-tree.js';
import { SearchIcon } from './shell-icons.js';

const GROUP_CLASS =
  'py-1.5 **:[[cmdk-group-heading]]:px-3 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-[11px] **:[[cmdk-group-heading]]:font-semibold **:[[cmdk-group-heading]]:tracking-wider **:[[cmdk-group-heading]]:text-text-tertiary **:[[cmdk-group-heading]]:uppercase';
const ITEM_CLASS =
  'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-text-secondary transition-colors hover:bg-background-gray-primary data-[selected=true]:bg-background-gray-primary data-[selected=true]:text-text-primary';

function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded-sm border border-card-border bg-card-background px-1.5 py-0.5 font-mono text-[10px] shadow-xs">
      {children}
    </kbd>
  );
}

function ResultItem({
  value,
  icon,
  title,
  hint,
  onSelect,
}: {
  value: string;
  icon: ReactNode;
  title: string;
  hint?: string;
  onSelect: () => void;
}) {
  return (
    <Command.Item value={value} onSelect={onSelect} className={ITEM_CLASS}>
      <span className="flex size-5 shrink-0 items-center justify-center text-icon-secondary">{icon}</span>
      <span className="min-w-0 flex-1 truncate text-text-primary">{title}</span>
      {hint && <span className="hidden shrink-0 truncate text-xs text-text-tertiary sm:inline">{hint}</span>}
    </Command.Item>
  );
}

/** Local entities only: a built-in or marketplace row has no page in this organization yet. */
const local = (rows: EntitySearchResult[] | undefined) => (rows ?? []).filter((row) => row.source === 'local');

/**
 * Global search (⌘K / Ctrl+K): the pages the person may open, plus agent templates, workflows and
 * employees found by the entity search API. Each entity type is searched only when the person may
 * open its page.
 */
export function CommandSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  const permissions = useAuthStore((s) => s.user?.permissions ?? []);
  const organizationPlan = useAuthStore((s) => s.user?.organizationPlan ?? null);
  const canViewAgents = usePolicy('CanViewAgents');
  const canManageWorkforce = usePolicy('CanManageWorkforce');

  const navTree = buildNavTree(useAllEnabledNavItems(), (policy) =>
    evaluateClientPolicy(policy, permissions, organizationPlan),
  );
  const templates = useEntitySearch('template', query, { enabled: open && canViewAgents, limit: 5 });
  const workflows = useEntitySearch('workflow', query, { enabled: open && canViewAgents, limit: 5 });
  const employees = useEntitySearch('employee', query, { enabled: open && canManageWorkforce, limit: 5 });

  // A press outside the open search closes it.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && rootRef.current?.contains(event.target)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const go = (path: string) => {
    setOpen(false);
    setQuery('');
    navigate(path);
  };

  const needle = query.trim().toLowerCase();
  const pages = [
    ...navTree.topItems.map((item) => ({ item, group: undefined as string | undefined })),
    ...navTree.groups.flatMap((group) => group.items.map((item) => ({ item, group: group.label }))),
  ].filter(({ item, group }) => !needle || `${group ?? ''} ${item.label}`.toLowerCase().includes(needle));

  return (
    // Template example: NextAdmin `components/common/header/searchbar.tsx`, reduced to its icon trigger (owner
    // 2026-10-17: the search box becomes an icon on the right that expands on click). Open, the box grows out of the
    // icon over the header (full width under xl) and the results drop below it.
    <div ref={rootRef} className="relative size-10 shrink-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search pages"
        aria-keyshortcuts="Meta+K"
        aria-expanded={open}
        title="Search (⌘K)"
        className={cn(
          'flex size-10 items-center justify-center rounded-lg border border-card-border bg-card-background text-icon-primary shadow-xs transition-colors outline-none hover:bg-background-gray-primary focus-visible:border-input-primary-focus-border focus-visible:ring-4 focus-visible:ring-input-primary-focus-border/20',
          open && 'invisible',
        )}
      >
        <SearchIcon />
      </button>

      {open && (
        <Command
          label="Global search"
          shouldFilter={false}
          onKeyDown={(event) => {
            if (event.key !== 'Escape') return;
            event.preventDefault();
            setOpen(false);
            setQuery('');
            triggerRef.current?.focus();
          }}
          className="fixed inset-x-4 top-4 z-50 motion-safe:animate-[search-expand_180ms_ease-out] xl:absolute xl:inset-x-auto xl:top-0 xl:right-0 xl:w-md"
        >
          <InputGroup className="h-10 bg-card-background shadow-xs">
            <InputGroupAddon align="inline-start" className="pr-0 text-icon-tertiary">
              <SearchIcon />
            </InputGroupAddon>
            <Command.Input
              autoFocus
              value={query}
              onValueChange={setQuery}
              placeholder="Search pages, templates, workflows…"
              className="w-full min-w-0 flex-1 border-none bg-transparent pl-2 text-sm text-text-primary outline-none placeholder:text-text-tertiary focus:ring-0"
            />
            <InputGroupAddon align="inline-end">
              <Kbd>ESC</Kbd>
            </InputGroupAddon>
          </InputGroup>

          <div className="absolute top-full right-0 left-0 mt-2 overflow-hidden rounded-xl border border-card-border bg-card-background text-text-primary shadow-2xl">
            <Command.List className="scrollbar-thin max-h-96 overflow-y-auto p-2">
              <Command.Empty className="py-8 text-center text-sm text-text-tertiary">
                Nothing matches “{query}”.
              </Command.Empty>

              {pages.length > 0 && (
                <Command.Group heading="Pages" className={GROUP_CLASS}>
                  {pages.map(({ item, group }) => {
                    const Icon = resolveIcon(item.icon);
                    return (
                      <ResultItem
                        key={item.path}
                        value={`page:${item.path}`}
                        icon={<Icon size={16} />}
                        title={group ? `${group} / ${item.label}` : item.label}
                        onSelect={() => go(item.path)}
                      />
                    );
                  })}
                </Command.Group>
              )}

              {local(templates.data).length > 0 && (
                <Command.Group heading="Agent templates" className={GROUP_CLASS}>
                  {local(templates.data).map((row) => (
                    <ResultItem
                      key={row.id}
                      value={`template:${row.id}`}
                      icon={<Bot size={16} />}
                      title={row.name}
                      hint={row.department ?? undefined}
                      onSelect={() => go(`/agents/templates/${row.slug}`)}
                    />
                  ))}
                </Command.Group>
              )}

              {local(workflows.data).length > 0 && (
                <Command.Group heading="Workflows" className={GROUP_CLASS}>
                  {local(workflows.data).map((row) => (
                    <ResultItem
                      key={row.id}
                      value={`workflow:${row.id}`}
                      icon={<Workflow size={16} />}
                      title={row.name}
                      hint={row.category ?? undefined}
                      onSelect={() => go(`/agents/workflows/${row.id}`)}
                    />
                  ))}
                </Command.Group>
              )}

              {local(employees.data).length > 0 && (
                <Command.Group heading="Employees" className={GROUP_CLASS}>
                  {local(employees.data).map((row) => (
                    <ResultItem
                      key={row.id}
                      value={`employee:${row.id}`}
                      icon={<Users size={16} />}
                      title={row.name}
                      hint={row.department ?? undefined}
                      onSelect={() => go(`/workforce/${row.id}`)}
                    />
                  ))}
                </Command.Group>
              )}
            </Command.List>

            <div className="flex items-center justify-between border-t border-card-border bg-background-gray-primary/40 px-4 py-2.5 text-xs text-text-tertiary">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <Kbd>↑</Kbd>
                  <Kbd>↓</Kbd>
                  <span>Navigate</span>
                </span>
                <span className="flex items-center gap-1">
                  <Kbd>↵</Kbd>
                  <span>Open</span>
                </span>
              </div>
              <span className="flex items-center gap-1">
                <FileText size={12} />
                <span>Pages and entities you can open</span>
              </span>
            </div>
          </div>
        </Command>
      )}
    </div>
  );
}
